import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Cpu, Flame, Sword, Move, Package, PlayCircle, ExternalLink } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.8 } } };

const categories = [
  {
    id: 'actions',
    icon: Move,
    title: 'Actions',
    intro: 'The verbs. Everything the player can do on the ground — movement, traversal, interaction.',
    items: [
      { name: 'Locomotion', note: 'Base movement with directional blend + animation state machine.' },
      { name: 'Sprint · Running', note: 'Toggleable sprint tied to stamina drain.' },
      { name: 'Crouch', note: 'Stealth-oriented crouch with a dipped camera rig.' },
      { name: 'Dodge', note: 'Directional dodge with i-frames for combat spacing.' },
      { name: 'Vault', note: 'Trace-driven traversal over waist-height obstacles.' },
      { name: 'Pick Up Equipment', note: 'Contextual interact prompt for gear on the ground.' },
    ],
  },
  {
    id: 'player-system',
    icon: Package,
    title: 'Player System',
    intro: 'The stat and inventory scaffolding — the RPG loops that hang off every action.',
    items: [
      { name: 'Progression · XP + Level', note: 'XP-to-level curve with a UI meter feeding future skill trees.' },
      { name: 'Stamina', note: 'Drains on sprint / traversal, regenerates on idle — pushes pacing.' },
      { name: 'HP · Death State', note: 'Health pool; zero HP transitions the character into death + input lock.' },
      { name: 'Inventory & Equipping', note: 'Swap and equip weapons from the inventory with a live on-body visual update.' },
    ],
  },
  {
    id: 'combat',
    icon: Sword,
    title: 'Combat',
    intro: 'What makes the fight feel alive — offense, reaction, and enemy behaviour.',
    items: [
      { name: 'Combo Attack + VFX', note: 'Chained light-attack combo with Niagara VFX per hit.' },
      { name: 'Assassination', note: 'Contextual takedown from behind on an unaware target — one-shot payoff.' },
      { name: 'Target Lock', note: 'Camera-locked focus for readable duels and precise inputs.' },
      { name: 'AI Patrol & Detection', note: 'Enemies patrol routes and engage on sight or sound — light stealth-vs-combat loop.' },
    ],
  },
];

export default function RPGSection() {
  return (
    <section id="rpg-lab" className="relative py-24 lg:py-32 border-t border-white/5" data-testid="rpg-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="flex items-center gap-4 mb-10">
          <Sparkles className="text-[#e58a59]" size={20} />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">Chapter 03 — RPG · Prototype</span>
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-zinc-500">Unreal Engine · Ongoing</span>
        </motion.div>

        {/* Sub-eyebrow */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-4 text-xs uppercase tracking-[0.3em] text-[#e58a59]" data-testid="rpg-eyebrow">
          A third-person action RPG prototype · Working title
        </motion.div>

        {/* Title */}
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-black text-white text-6xl sm:text-7xl lg:text-[9rem] leading-[0.95] tracking-tighter mb-8" data-testid="rpg-title">
          Asha.<br />
          <span className="italic font-light text-[#e58a59] text-4xl sm:text-5xl lg:text-6xl">A brighter tomorrow.</span>
        </motion.h2>

        {/* Logline */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-white/10 bg-[#101012] p-8 lg:p-12 mb-16 relative overflow-hidden" data-testid="rpg-logline">
          <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#e58a59' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <Flame size={16} className="text-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Logline — Story Spine</span>
            </div>
            <p className="font-display font-light text-white text-2xl lg:text-4xl leading-[1.25] max-w-4xl">
              She spent one life being told she was <span className="italic text-[#e58a59]">nothing</span>. She was given a second one to prove them wrong.
            </p>
            <div className="text-zinc-400 text-base lg:text-lg leading-relaxed mt-8 max-w-3xl space-y-4">
              <p>
                <span className="text-white font-semibold">Asha</span> — <span className="italic">&ldquo;hope&rdquo;</span> in Sanskrit — lived her first life as a shadow of herself. Bullied at school, unseen at home, she carried the quiet hurt of a life that never asked her opinion. One afternoon, lost inside her own head, she stepped onto a road. The truck did not slow down.
              </p>
              <p>
                She opened her eyes in unfamiliar arms — a mother&rsquo;s, a father&rsquo;s — young, warm, and terrified. She was an infant again. And the world she had been reborn into was one that held magic like a birthright.
              </p>
              <p>
                Fifteen years pass in a village that will one day matter — every kind neighbour, every difficult sibling, every shopkeeper&rsquo;s daughter is a seed for what comes later. On her fifteenth birthday, Asha makes the only choice her first life never let her make:
              </p>
              <p className="text-white text-lg lg:text-xl italic pt-2">
                She becomes an adventurer. <span className="text-[#e58a59] not-italic font-semibold">This time, she will live.</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Credits & Influences */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="rpg-credits"
        >
          <div className="p-6 rounded-xl border border-white/10 bg-[#101012]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] font-semibold">Following</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed">
              I&rsquo;m building this prototype alongside a YouTube tutorial series by <a href="https://www.youtube.com/@GorkaGames" target="_blank" rel="noreferrer" className="text-white font-semibold underline decoration-[#e58a59]/40 underline-offset-4 hover:decoration-[#e58a59] transition-colors">Gorka Games</a> that lays down an <span className="text-white">Assassin&rsquo;s Creed</span>-flavoured baseline. Every system is then re-approached and rewritten to fit Asha&rsquo;s world — not copy-pasted.
            </p>
          </div>
          <div className="p-6 rounded-xl border border-white/10 bg-[#101012]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] font-semibold">Narrative Influences</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {['Black Clover', 'Kingdom', 'Solo Leveling'].map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-[#1a1a1c] text-xs text-zinc-200">
                  {s}
                </span>
              ))}
            </div>
            <p className="text-zinc-400 text-xs leading-relaxed">
              Tone and pacing borrow from these anime — underdog rise, world-building weight, and the quiet cost of ambition.
            </p>
          </div>
        </motion.div>

        {/* Prototype video showcase */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="mb-16"
          data-testid="rpg-video-section"
        >
          <div className="flex items-center gap-4 mb-6">
            <PlayCircle className="text-[#e58a59]" size={18} />
            <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">Prototype in Motion</span>
            <div className="flex-1 h-px bg-white/10" />
            <a
              href="https://youtu.be/yqhPS0awLy8"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-xs text-zinc-300 hover:border-[#e58a59]/50 hover:text-[#e58a59] transition-colors"
              data-testid="rpg-video-yt-link"
            >
              Watch on YouTube <ExternalLink size={12} />
            </a>
          </div>

          <div
            className="relative rounded-xl overflow-hidden border border-white/10 bg-black aspect-video glow-card"
            data-testid="rpg-video-frame"
          >
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube-nocookie.com/embed/yqhPS0awLy8?rel=0&modestbranding=1"
              title="Asha — RPG Prototype (Gorka Games series)"
              frameBorder="0"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          <p className="text-zinc-500 text-xs mt-3 italic">
            Video from my ongoing RPG series — press play to watch, use the YouTube controls for volume and fullscreen.
          </p>
        </motion.div>

        {/* Mechanics header */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="flex items-center gap-4 mb-8">
          <Sword className="text-[#e58a59]" size={18} />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">Mechanics · Prototyped</span>
          <div className="flex-1 h-px bg-white/10" />
        </motion.div>

        {/* Three category boxes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="rpg-categories">
          {categories.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative p-7 rounded-xl border border-white/10 bg-[#101012] glow-card flex flex-col"
              data-testid={`rpg-cat-${c.id}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-lg border border-[#e58a59]/25 bg-[#e58a59]/10 flex items-center justify-center">
                  <c.icon size={19} className="text-[#e58a59]" />
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#e58a59]/30 bg-[#e58a59]/10 text-[#e58a59] text-[9px] uppercase tracking-[0.22em] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59] animate-pulse" /> Live
                </span>
              </div>
              <h4 className="font-display text-2xl text-white font-bold mb-2 leading-tight">{c.title}</h4>
              <p className="text-zinc-400 text-sm leading-relaxed mb-5">{c.intro}</p>
              <ul className="mt-auto space-y-3 border-t border-white/10 pt-5">
                {c.items.map((it) => (
                  <li key={it.name} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e58a59] shrink-0" />
                    <div>
                      <div className="text-sm text-white font-medium leading-tight">{it.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{it.note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* HUD note */}
        <div className="mt-6 p-6 rounded-xl border border-dashed border-white/10 bg-[#101012]" data-testid="rpg-hud-note">
          <div className="flex items-center gap-3 mb-2">
            <Cpu size={14} className="text-[#e58a59]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#e58a59]">HUD</span>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-2xl">
            A lightweight combat HUD reads the three vital loops at a glance — <span className="text-white">XP + level</span>, <span className="text-white">stamina</span> and <span className="text-white">HP</span>. Sprinting or dodging spends stamina; empty stamina forces a walk; empty HP ends the run.
          </p>
        </div>
      </div>
    </section>
  );
}
